import {
  ProcessingItemState,
  useActionButtonContext,
} from '@aibees/elephant/context/action-button-context';
import { PeopleTableInfo } from '@aibees/elephant/feature/search-table-people';
import { SaveRecordAction } from '@aibees/elephant/hooks';
import { useContactDetailsEnrichmentStatus } from '@aibees/elephant/hooks/people/use-contact-details-enrichment-status';
import { useSaveRecord } from '@aibees/elephant/hooks/use-save-record';
import { usePersonUpdatesStore } from '@aibees/elephant/store/person-updates';
import { SearchType } from '@aibees/elephant/types';
import { notificationIconMap } from '@aibees/elephant/utils';
import { useAuthContext } from '@aibees/shared/auth';
import { useNotify } from '@aibees/shared/hooks';
import { useWorkspaceUsers } from '@aibees/shared/workspace';
import { useIsPeopleUnlockButtonLoading } from '../../people-unlock/hooks/use-is-people-unlock-button-loading';
import { QUICK_INFO_BUTTONS } from '../components/quick-info-button';

export function useHandleContactDetailsEnrichment(
  data?: PeopleTableInfo,
  onSuccess?: () => void
) {
  const { user } = useAuthContext();
  const notify = useNotify();
  const users = useWorkspaceUsers(true);
  const { addProcessingItems } = useActionButtonContext();

  const api = useSaveRecord(SearchType.People);
  const { unlocked, failed, disabled } = useContactDetailsEnrichmentStatus(
    api,
    data
  );
  const { loadingContactDetails } = useIsPeopleUnlockButtonLoading(data);
  const { setProfileUpdateTriggered } = usePersonUpdatesStore();

  const handleEnrichContactDetails = ({
    updateMode,
    updateJobChangeMode,
  }: {
    updateMode?: boolean;
    updateJobChangeMode?: boolean;
  }): void => {
    if (loadingContactDetails) return;
    if (updateMode) {
      setProfileUpdateTriggered(true);
    }
    // A company change moves the email too — the server refreshes the work
    // history and deletes the current email as part of the same run — so the
    // Contact Details side has to show progress rather than sit on a stale
    // update bell until the socket lands.
    //
    // Not gated on the email being unlocked: the bell itself is raised by the
    // company change alone, whether or not an email was ever found, so the
    // in-progress state has to cover exactly the same records.
    if (updateJobChangeMode) {
      addProcessingItems({
        id: data?.id ?? '',
        type: QUICK_INFO_BUTTONS.EMAIL_ENRICHMENT,
        state: ProcessingItemState.CHECKING_FOR_UPDATE,
      });
    }

    const selectedUser = users.find((u) => u.id === user?.id);

    // One arm per case, in precedence order. `updateJobChangeMode` is the
    // company change; the unlock state is derived from `data` here rather than
    // threaded in as an extra parameter.
    const resolveAction = () => {
      // A company change already refreshes the whole work history server-side,
      // locked or not, so it needs no second action alongside it.
      if (updateJobChangeMode)
        return SaveRecordAction.CONTACT_UPDATE_JOB_CHANGE;
      if (!updateMode) return SaveRecordAction.CONTACT_REVEAL_PROFILE;
      // Everything else is the broad profile update. A locked profile with an
      // experience change and no company change lands here too — there is no
      // narrower action to send it, so its work history may not move at all.
      return SaveRecordAction.CONTACT_UPDATE_PROFILE;
    };

    const action = resolveAction();

    // The processing state follows the action rather than re-deriving the same
    // branches. CONTACT_UPDATE_JOB_CHANGE reports UPDATING_JOB_CHANGE: its three
    // consumers all key off "the work history is refreshing on a locked
    // profile" — most importantly the completion signal, since a locked profile
    // never sets revelation.contact.state and would otherwise spin forever.
    const isWorkHistoryOnlyUpdate =
      action === SaveRecordAction.CONTACT_UPDATE_JOB_CHANGE;

    addProcessingItems({
      id: data?.id ?? '',
      type: QUICK_INFO_BUTTONS.CONTACT_ENRICHMENT,
      state: isWorkHistoryOnlyUpdate
        ? ProcessingItemState.UPDATING_JOB_CHANGE
        : updateMode
        ? ProcessingItemState.UPDATING
        : ProcessingItemState.UNLOCKING,
    });

    api
      .mutateAsync({
        kind: 'ADD',
        actions: [action],
        ids: [data?.id || ''],
        overwriteOwner: false,
        user: selectedUser,
        labels: [],
      })
      .then(() => {
        const key = Date.now().toString();
        notify.progress(
          'Your request is in progress',
          'Processing the profile insight. Please wait...',
          key,
          {
            icon: notificationIconMap.ProfileInsight,
          }
        );
        onSuccess?.();
      });
  };

  return {
    handleEnrichContactDetails,
    loading: loadingContactDetails,
    unlocked,
    failed,
    disabled,
  };
}
